/** @jsxImportSource @emotion/react */
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { SyntheticEvent, useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'

import CheckboxComponent from 'components/atoms/Checkbox'
import Toast from 'components/atoms/Toast'
import { ChargeSection } from 'components/lab/billingCharge/BillingChargeUpsert'
import { getBillingTestTypeLabel } from 'components/lab/billingCharge/utils'
import {
  useAddChargeAttachmentsMutation,
  useGetPatientChargeDocumentsQuery,
  useLazyGetChargeFileUrlQuery,
  useRetryChargeAttachmentMutation,
} from 'store/services/billingCharges'
import { BillingChargeDto, ChargeDocument } from 'store/types'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from 'components/lab/user/permissions'

interface IProps {
  charge: BillingChargeDto
  patientId: string
  mocked: boolean
  onRetried?: () => void
}

const STATUS_STYLES: Record<string, { bg: string; color: string; label: string }> = {
  PENDING: { bg: '#fff3cd', color: '#856404', label: 'Pending' },
  SENT: { bg: '#d4edda', color: '#155724', label: 'Sent' },
  FAILED: { bg: '#f8d7da', color: '#721c24', label: 'Failed' },
}

export const ChargeFilesSection = ({ charge, patientId, mocked, onRetried }: IProps) => {
  const files = charge.files || []
  const [getFileUrl] = useLazyGetChargeFileUrlQuery()
  const [retryAttachment, { isLoading: retrying }] = useRetryChargeAttachmentMutation()
  const [addAttachments, { isLoading: adding }] = useAddChargeAttachmentsMutation()

  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [expandedOrders, setExpandedOrders] = useState<number[]>([])

  const { data: patientDocuments, isFetching: documentsLoading } =
    useGetPatientChargeDocumentsQuery(showAddModal ? { patientId } : skipToken)

  // Only patient documents that are not yet attached to this charge can be added.
  const attachedKeys = files.map((file) => file.key)
  const availableDocuments = (patientDocuments || []).filter(
    (doc) => !attachedKeys.includes(doc.key),
  )

  const documentsByOrder = availableDocuments.reduce<
    Array<{ orderId: number; orderTestType?: string; documents: ChargeDocument[] }>
  >((groups, doc) => {
    const group = groups.find((g) => g.orderId === doc.orderId)
    if (group) {
      group.documents.push(doc)
    } else {
      groups.push({ orderId: doc.orderId, orderTestType: doc.orderTestType, documents: [doc] })
    }
    return groups
  }, [])

  // With few orders there is no need to make the user expand them one by one:
  // start with every group expanded. Larger lists start collapsed.
  useEffect(() => {
    if (showAddModal && documentsByOrder.length && documentsByOrder.length <= 5) {
      setExpandedOrders(documentsByOrder.map((group) => group.orderId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddModal, patientDocuments])

  // Quick/patient charges have no Optum claim, so attachments don't apply.
  const canModifyCharge = useLabPermission(UserPermission.MODIFY_BILLING_CHARGES)
  const canAddDocuments = charge.type === 'DEFAULT' && canModifyCharge

  const viewFile = async (key: string) => {
    try {
      const { url } = await getFileUrl({ patientId, chargeId: charge.id, key }).unwrap()
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (e: any) {
      Toast({ type: 'error', label: e?.data?.message || 'Failed to open file' })
    }
  }

  const retryFile = async (fileKey: string) => {
    try {
      await retryAttachment({ patientId, chargeId: charge.id, fileKey, mocked }).unwrap()
      Toast({ type: 'success', label: 'Attachment resubmitted to Optum.' })
    } catch (e: any) {
      Toast({ type: 'error', label: e?.data?.message || 'Failed to retry attachment' })
    } finally {
      onRetried?.()
    }
  }

  const openAddModal = () => {
    setSelectedKeys([])
    setExpandedOrders([])
    setShowAddModal(true)
  }

  const toggleDocument = (key: string, checked: boolean) =>
    setSelectedKeys((prev) => (checked ? [...prev, key] : prev.filter((k) => k !== key)))

  const toggleOrderExpanded = (orderId: number) =>
    setExpandedOrders((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId],
    )

  // Order-level checkbox selects/deselects every document of that order. It reads as
  // checked only while all of them are selected, so unchecking a single document
  // switches it back off.
  const toggleOrderDocuments = (documents: ChargeDocument[], checked: boolean) => {
    const orderKeys = documents.map((doc) => doc.key)
    setSelectedKeys((prev) =>
      checked
        ? [...prev, ...orderKeys.filter((key) => !prev.includes(key))]
        : prev.filter((key) => !orderKeys.includes(key)),
    )
  }

  // The charge already has a claim at Optum: the new attachments are submitted
  // immediately. Otherwise they stay pending until the claim is submitted.
  const submitsImmediately = !!charge.claimControlNumber

  const addSelectedDocuments = async () => {
    try {
      await addAttachments({
        patientId,
        chargeId: charge.id,
        fileKeys: selectedKeys,
        mocked,
      }).unwrap()
      Toast({
        type: 'success',
        label: submitsImmediately
          ? 'Documents attached and submitted to Optum.'
          : 'Documents attached. They will be sent to Optum once the claim is submitted.',
      })
      setShowAddModal(false)
    } catch (e: any) {
      Toast({ type: 'error', label: e?.data?.message || 'Failed to add documents' })
      setShowAddModal(false)
    } finally {
      onRetried?.()
    }
  }

  return (
    <ChargeSection title={'Attached Documents'}>
      <div className="d-flex flex-column" css={{ gap: 8 }}>
        {files.length > 0 && (
          <div className="d-flex align-items-center text-muted px-2" css={{ fontSize: 12 }}>
            <div css={{ flex: 2 }}>File</div>
            <div css={{ flex: 1 }}>Type</div>
            <div css={{ width: 110 }}>Status</div>
            <div css={{ width: 90 }} />
          </div>
        )}
        {files.length === 0 && (
          <div className="font-italic text-muted px-2">
            No documents are attached to this charge.
          </div>
        )}
        {files.map((file) => {
          const status = STATUS_STYLES[file.status] ?? STATUS_STYLES.PENDING

          return (
            <div
              key={file.key}
              className="d-flex align-items-center p-2"
              css={{ background: '#fff', borderRadius: 5 }}>
              <div css={{ flex: 2 }}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    viewFile(file.key)
                  }}>
                  {file.fileName}
                </a>
                {file.status === 'FAILED' && file.error && (
                  <div
                    className="text-danger mt-1"
                    css={{ fontSize: 12, wordBreak: 'break-word' }}>
                    {file.error}
                  </div>
                )}
              </div>
              <div css={{ flex: 1 }}>{file.fileType}</div>
              <div css={{ width: 110 }}>
                <span
                  css={{
                    background: status.bg,
                    color: status.color,
                    borderRadius: 4,
                    padding: '2px 8px',
                    fontSize: 12,
                    fontWeight: 600,
                  }}>
                  {status.label}
                </span>
              </div>
              <div css={{ width: 90 }}>
                {file.status === 'FAILED' && canModifyCharge && (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={retrying}
                    onClick={() => retryFile(file.key)}>
                    Retry
                  </Button>
                )}
              </div>
            </div>
          )
        })}
        {canAddDocuments && (
          <div>
            <Button size="sm" variant="outline-primary" type="button" onClick={openAddModal}>
              Add Documents
            </Button>
          </div>
        )}
      </div>
      <Modal centered size="lg" show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header>
          <Modal.Title>Add Documents</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted mb-2" css={{ fontSize: 14 }}>
            Select documents from the patient's orders to attach to this charge.{' '}
            {submitsImmediately
              ? 'They will be submitted to Optum immediately.'
              : 'They will be sent to Optum once the claim is submitted.'}
          </div>
          {documentsLoading ? (
            <div className="text-muted">Loading documents…</div>
          ) : !availableDocuments.length ? (
            <div className="font-italic text-muted">
              No more documents are available for this patient.
            </div>
          ) : (
            <div className="d-flex flex-column" css={{ gap: 6 }}>
              {documentsByOrder.map(({ orderId, orderTestType, documents }) => {
                const expanded = expandedOrders.includes(orderId)
                const selectedCount = documents.filter((doc) =>
                  selectedKeys.includes(doc.key),
                ).length
                const allSelected = selectedCount === documents.length
                const testTypeLabel = getBillingTestTypeLabel(orderTestType)

                return (
                  <div key={orderId} css={{ border: '1px solid #ced4da', borderRadius: 4 }}>
                    <div
                      className="d-flex align-items-center p-2"
                      css={{ '& > svg': { cursor: 'pointer' } }}>
                      {expanded ? (
                        <KeyboardArrowDownIcon onClick={() => toggleOrderExpanded(orderId)} />
                      ) : (
                        <KeyboardArrowUpIcon onClick={() => toggleOrderExpanded(orderId)} />
                      )}
                      <CheckboxComponent
                        parentClassName="custom-control d-flex align-items-center mx-2"
                        css={{ height: 16, width: 16 }}
                        value={allSelected}
                        handleChange={(e: SyntheticEvent) =>
                          toggleOrderDocuments(
                            documents,
                            (e.target as HTMLInputElement).checked,
                          )
                        }
                      />
                      <span
                        className="font-weight-bold"
                        css={{ fontSize: 14, cursor: 'pointer' }}
                        onClick={() => toggleOrderExpanded(orderId)}>
                        Order #{orderId}
                        {testTypeLabel ? ` — ${testTypeLabel}` : ''}
                      </span>
                      <span className="text-muted ml-auto" css={{ fontSize: 12 }}>
                        {selectedCount ? `${selectedCount} of ` : ''}
                        {documents.length} {documents.length === 1 ? 'document' : 'documents'}
                        {selectedCount ? ' selected' : ''}
                      </span>
                    </div>
                    {expanded && (
                      <div
                        className="d-flex flex-column px-2 pb-2"
                        css={{ gap: 4, marginLeft: 32 }}>
                        {documents.map((doc) => (
                          <div key={doc.key} className="d-flex align-items-center">
                            <CheckboxComponent
                              parentClassName="custom-control d-flex align-items-center mr-2"
                              css={{ height: 16, width: 16 }}
                              value={selectedKeys.includes(doc.key)}
                              handleChange={(e: SyntheticEvent) =>
                                toggleDocument(
                                  doc.key,
                                  (e.target as HTMLInputElement).checked,
                                )
                              }
                            />
                            <span className="font-weight-bold mr-2" css={{ fontSize: 13 }}>
                              {doc.fileType}
                            </span>
                            <span
                              className="text-muted"
                              css={{ fontSize: 13, wordBreak: 'break-all' }}>
                              {doc.fileName}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowAddModal(false)}
            disabled={adding}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={adding || !selectedKeys.length}
            onClick={addSelectedDocuments}>
            {adding
              ? 'Adding…'
              : selectedKeys.length
                ? `Add ${selectedKeys.length} ${selectedKeys.length === 1 ? 'Document' : 'Documents'}`
                : 'Add Documents'}
          </Button>
        </Modal.Footer>
      </Modal>
    </ChargeSection>
  )
}
